import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Workflow, Download, FileText, Mail, ArrowRight,
  Cpu, Layers, GitBranch, Settings2, ShieldCheck, FileCheck2,
  Eye, Compass, ListChecks, Send,
  Building2, CircuitBoard, Factory, Rocket, ShieldAlert, Users,
  Zap, LifeBuoy, Network, Handshake,
  Activity, FileDown, Radio
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { companyInfo } from '../data/mockData';

const dataConnects = [
  { icon: Cpu,          title: 'CAD / ECAD',        body: 'Schematic, layout, stack-up, placement, routing.' },
  { icon: Layers,       title: 'BOM / AML',         body: 'Components, alternates, source status, datasheet evidence.' },
  { icon: GitBranch,    title: 'PDM / PLM',         body: 'Revisions, ECO/ECN, approval records, configuration state.' },
  { icon: Settings2,    title: 'CAM / NPI',         body: 'Manufacturing package, process notes, first-article readiness.' },
  { icon: ShieldCheck,  title: 'Supplier Evidence', body: 'Provenance, qualification, chain of custody, source confidence.' },
  { icon: FileCheck2,   title: 'Decision File',     body: 'Risk matrix, action recommendation, executive-ready memo.' }
];

const oodaSteps = [
  { icon: Eye,        step: 'Observe', body: 'Collect CAD, BOM, PDM, CAM, supplier, and field evidence.' },
  { icon: Compass,    step: 'Orient',  body: 'Map reliability, provenance, dependency, and manufacturing-risk signals.' },
  { icon: ListChecks, step: 'Decide',  body: 'Rank actions: accept, accept with conditions, quarantine, reject, qualify, redesign.' },
  { icon: Send,       step: 'Act',     body: 'Deliver a concise evidence-backed decision file for engineering and leadership.' }
];

const capabilityPackets = [
  { id: 1,  label: 'Page 1 — Intro / Book Intro Call',                  file: '/SALAR_MissionThread_01_Intro_Book_Call.pdf',                          size: '215 KB', featured: false },
  { id: 2,  label: 'Page 2 — Capability Statement',                      file: '/SALAR_MissionThread_02_Capability_Statement.pdf',                     size: '208 KB', featured: false },
  { id: 3,  label: 'Page 3 — Quad Chart',                                file: '/SALAR_MissionThread_03_Quad_Chart.pdf',                               size: '162 KB', featured: false },
  { id: 4,  label: 'Full 3-Page Mission Thread Capability Packet',       file: '/SALAR_MissionThread_3_Page_Capability_Packet.pdf',                    size: '585 KB', featured: true  },
  { id: 5,  label: 'Small Defense Suppliers Packet',                     file: '/SALAR_MissionThread_Small_Defense_Suppliers_3_Page.pdf',              size: '585 KB', featured: false },
  { id: 6,  label: 'PCB Design Houses Packet',                           file: '/SALAR_MissionThread_PCB_Design_Houses_3_Page.pdf',                    size: '585 KB', featured: false },
  { id: 7,  label: 'EMS / Contract Manufacturers Packet',                file: '/SALAR_MissionThread_EMS_Contract_Manufacturers_3_Page.pdf',           size: '585 KB', featured: false },
  { id: 8,  label: 'Aerospace / Robotics Startups Packet',               file: '/SALAR_MissionThread_Aerospace_Robotics_Startups_3_Page.pdf',          size: '585 KB', featured: false },
  { id: 9,  label: 'DLA / Defense Sustainment Packet',                   file: '/SALAR_MissionThread_DLA_Defense_Sustainment_3_Page.pdf',              size: '585 KB', featured: false },
  { id: 10, label: 'CAD / PDM Consultants Packet',                       file: '/SALAR_MissionThread_CAD_PDM_Consultants_3_Page.pdf',                  size: '585 KB', featured: false }
];

const pilotOptions = [
  { icon: Zap,       title: 'ReleaseShield Sprint',  body: '10-business-day CAD/BOM/PDM/CAM review.' },
  { icon: LifeBuoy,  title: 'Sustainment Rescue Pack', body: 'Obsolete or hard-to-source part-family decision package.' },
  { icon: Network,   title: 'Qualification Graph',   body: 'Supplier / provenance / dependency mapping.' },
  { icon: Handshake, title: 'Partner Layer',          body: 'CAD/PDM/PLM consultant assurance add-on.' }
];

const audiences = [
  { icon: ShieldAlert,   title: 'Small Defense Suppliers' },
  { icon: CircuitBoard,  title: 'PCB Design Houses' },
  { icon: Factory,       title: 'EMS / Contract Manufacturers' },
  { icon: Rocket,        title: 'Aerospace / Robotics Startups' },
  { icon: Building2,     title: 'DLA / Defense Sustainment Teams' },
  { icon: Users,         title: 'CAD / PDM / PLM Consultants' }
];

const MissionThread = () => {
  const bookIntroHref = `mailto:${companyInfo.emails.general}?subject=${encodeURIComponent('Book Intro Call — SALAR Mission Thread')}&body=${encodeURIComponent(
    "I'd like to schedule an intro call to discuss SALAR Mission Thread.\n\n— Program / organization:\n— Domain (electronics / supply-chain / manufacturing / sustainment):\n— One board, BOM, or sustainment problem you'd like a decision file for:\n— Preferred call time(s):"
  )}`;

  return (
    <div className="min-h-screen" style={{ background: '#0a1628' }}>
      <Helmet>
        <title>SALAR Mission Thread | CAD-PDM-CAM Engineering Assurance</title>
        <meta name="description" content="SALAR Mission Thread helps mission-critical electronics teams connect CAD, BOM, PDM, supplier, CAM, and sustainment evidence into decision-ready capability and quad-chart packages." />
        <meta property="og:title" content="From Design Files to Decision Files" />
        <meta property="og:description" content="A CAD-PDM-CAM assurance layer for release readiness, supplier confidence, manufacturing readiness, and sustainment decisions." />
        <meta property="og:url" content="https://www.salartechnologies.com/mission-assurance" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="SALAR Mission Assurance | CAD-PDM-CAM Engineering Assurance" />
        <meta name="twitter:description" content="Turn CAD, BOM, PDM, supplier, CAM, and sustainment evidence into decision-ready capability packages." />
        <link rel="canonical" href="https://www.salartechnologies.com/mission-assurance" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      {/* Hero */}
      <section
        className="text-white py-20 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/server_room_close.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="mission-thread-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto text-center">
            <Workflow className="w-20 h-20 mx-auto mb-6 text-cyan-300" />
            <Badge variant="outline" className="border-cyan-400 text-cyan-200 mb-4">
              CAD · ECAD · BOM · PDM/PLM · Supplier · CAM · Test
            </Badge>
            <h1 className="text-4xl lg:text-6xl font-bold mb-3">
              SALAR Mission Thread<sup className="text-2xl text-cyan-300 ml-1">™</sup>
            </h1>
            <p className="text-2xl lg:text-3xl text-cyan-200 font-light italic mb-6">
              From Design Files to Decision Files
            </p>
            <p className="text-lg text-blue-100 leading-relaxed max-w-3xl mx-auto mb-8">
              SALAR Mission Thread is a CAD-PDM-CAM engineering assurance layer for mission-critical electronics. It
              helps teams convert CAD/ECAD files, BOMs, PDM/PLM records, supplier evidence, manufacturing signals, and
              sustainment questions into decision-ready outputs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={bookIntroHref} data-testid="hero-book-intro-call">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Mail className="mr-2" size={20} />
                  Book Intro Call
                </Button>
              </a>
              <a href="#capability-packets" data-testid="hero-download-capability-packet">
                <Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]">
                  <Download className="mr-2" size={20} />
                  Download Capability Packet
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1 — What it does */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-6 text-center">What SALAR Mission Thread Does</h2>
            <Card className="border-blue-500/30 bg-blue-900/20 backdrop-blur-sm">
              <CardContent className="p-8 md:p-10">
                <p className="text-lg text-blue-100 leading-relaxed">
                  Mission Thread does not replace CAD, PDM, PLM, CAM, ERP, or MES platforms. It sits above them as an
                  engineering assurance layer. SALAR connects design evidence, supplier confidence, manufacturing
                  readiness, and sustainment logic into concise <span className="text-cyan-300 font-semibold">decision files</span>
                  &nbsp;that support release, hold, quarantine, qualification, redesign, or build decisions.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 2 — What Data It Connects */}
      <section
        className="py-16 border-y border-blue-800/30 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/circuit_chip.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed',
        }}
        data-testid="section-data-connects"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3 text-center">What Data It Connects</h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              Six structured evidence streams. Mission Thread reads from your existing systems and produces one
              decision-ready output.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {dataConnects.map(({ icon: Icon, title, body }) => (
                <Card key={title} data-testid={`data-card-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="border-blue-500/30 bg-blue-900/20 hover:bg-blue-900/30 transition-all">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded bg-gradient-to-br from-blue-600 to-cyan-700 border-2 border-blue-400/30 flex items-center justify-center flex-shrink-0">
                        <Icon className="text-white" size={22} />
                      </div>
                      <CardTitle className="text-lg text-white">{title}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-blue-100 leading-relaxed text-sm">{body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — Decision Workflow */}
      <section
        className="py-16 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/pexels_fiber_cables.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="section-decision-workflow"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3 text-center">Decision Workflow</h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              A disciplined four-step loop that converts technical and supply-chain evidence into release-grade
              decisions.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {oodaSteps.map(({ icon: Icon, step, body }, idx) => (
                <Card key={step} data-testid={`workflow-${step.toLowerCase()}`} className="border-cyan-500/30 bg-gradient-to-br from-blue-900/30 to-cyan-900/20 hover:from-blue-900/50 hover:to-cyan-900/30 transition-all">
                  <CardHeader>
                    <Badge variant="outline" className="border-cyan-400 text-cyan-200 w-fit mb-2">
                      Step {idx + 1}
                    </Badge>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-600 to-blue-700 border-2 border-cyan-300/40 flex items-center justify-center flex-shrink-0">
                        <Icon className="text-white" size={22} />
                      </div>
                      <CardTitle className="text-xl text-white">{step}</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-blue-100 leading-relaxed text-sm">{body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 — Download Capability Packets */}
      <section className="py-16 bg-blue-950/30" id="capability-packets" data-testid="section-packets">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3 text-center">Download the Capability Packages</h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              Short, polished, public-safe capability documents. Suitable for inclusion in BAA / topic responses,
              prime-contractor supplier diligence, and program-office briefing folders.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {capabilityPackets.map((p) => (
                <Card
                  key={p.id}
                  data-testid={`packet-${p.id}`}
                  className={`backdrop-blur-sm transition-all ${
                    p.featured
                      ? 'border-cyan-400/60 bg-gradient-to-br from-blue-900/50 to-cyan-900/40 shadow-lg shadow-cyan-500/10'
                      : 'border-blue-500/30 bg-blue-900/20 hover:bg-blue-900/30'
                  }`}
                >
                  <CardHeader>
                    <div className="flex items-start gap-3">
                      <div
                        className={`w-12 h-12 rounded flex items-center justify-center border-2 flex-shrink-0 ${
                          p.featured
                            ? 'bg-gradient-to-br from-cyan-600 to-blue-700 border-cyan-300/40'
                            : 'bg-gradient-to-br from-blue-600 to-cyan-700 border-blue-400/30'
                        }`}
                      >
                        <FileText className="text-white" size={22} />
                      </div>
                      <div className="flex-1">
                        <Badge variant="outline" className={`mb-2 text-xs ${p.featured ? 'border-cyan-300 text-cyan-200' : 'border-blue-400 text-blue-200'}`}>
                          PDF · {p.size}{p.featured && ' · Recommended'}
                        </Badge>
                        <CardTitle className="text-base text-white leading-snug">{p.label}</CardTitle>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <a href={p.file} target="_blank" rel="noopener noreferrer" data-testid={`packet-download-${p.id}`}>
                      <Button
                        size="sm"
                        className={p.featured ? 'bg-cyan-600 hover:bg-cyan-700 text-white w-full' : 'bg-blue-600 hover:bg-blue-700 text-white w-full'}
                      >
                        <Download className="mr-2" size={16} />
                        Download PDF
                      </Button>
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 — Best First Pilot */}
      <section
        className="py-16 relative"
        style={{
          backgroundImage: `linear-gradient(rgba(10,22,40,0.6), rgba(10,22,40,0.85)), url('/img/pexels_server_room.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="section-pilot"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-3 text-center">Best First Pilot</h2>
            <p className="text-center text-blue-100 mb-10 max-w-4xl mx-auto leading-relaxed">
              The recommended first pilot is one board, one BOM, one supplier-risk question, one alternate-part question,
              or one manufacturing-readiness package. SALAR reviews the evidence and returns a concise decision file that
              can be used by engineering, sourcing, manufacturing, quality, sustainment, or customer-facing teams.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {pilotOptions.map(({ icon: Icon, title, body }) => (
                <Card key={title} data-testid={`pilot-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`} className="border-blue-500/30 bg-blue-900/20 hover:bg-blue-900/30 transition-all">
                  <CardContent className="p-6">
                    <Icon className="w-10 h-10 text-cyan-300 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">{title}</h3>
                    <p className="text-blue-100 text-sm leading-relaxed">{body}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 6 — Who It Is Built For */}
      <section className="py-16 bg-white/5 backdrop-blur-sm border-y border-blue-800/30" data-testid="section-audience">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-10 text-center">Who It Is Built For</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {audiences.map(({ icon: Icon, title }) => (
                <div
                  key={title}
                  data-testid={`audience-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                  className="flex items-center gap-4 p-5 bg-blue-900/30 rounded-lg border border-blue-500/30 hover:border-cyan-400/50 transition-colors"
                >
                  <div className="w-12 h-12 rounded bg-gradient-to-br from-cyan-600 to-blue-700 border-2 border-cyan-300/30 flex items-center justify-center flex-shrink-0">
                    <Icon className="text-white" size={22} />
                  </div>
                  <span className="text-white font-semibold">{title}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === PCIe 6.0 / PAM4 Link Assurance — high-speed interconnect feature section === */}
      <section
        id="pcie-pam4-link-assurance"
        className="py-16 md:py-20 scroll-mt-20"
        style={{ background: 'linear-gradient(180deg, #0A1628 0%, #0F1A2B 100%)' }}
        data-testid="section-pcie-pam4"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-10">
              <div className="text-cyan-300 text-xs font-bold tracking-widest uppercase mb-3" data-testid="pcie-eyebrow">
                High-Speed Link Assurance
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight">
                PCIe 6.0 / PAM4 Link Assurance
              </h2>
              <p className="text-lg md:text-xl font-semibold text-cyan-200 mb-4">
                From eye diagram evidence to mission-ready interconnect decisions.
              </p>
              <p className="text-base text-blue-100 leading-relaxed max-w-4xl mx-auto">
                PCIe 6.0 and PAM4 signaling compress timing and voltage margin. A link may pass a narrow
                protocol view while hidden risk remains in board stackup, connectors, packages, supplier
                substitutions, manufacturing drift, firmware / equalization settings, and validation
                evidence gaps. SALAR positions this capability as an independent assurance-intelligence
                layer that turns mixed link, board, provenance, and manufacturing evidence into a
                repeatable risk score, mitigation order, and partner-ready decision record.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mb-10">
              {/* Hero visual — PAM4 eye diagram + waveforms (inline SVG, no stock photo) */}
              <div className="lg:col-span-7 order-2 lg:order-1">
                <div
                  className="rounded-2xl overflow-hidden border-2 border-cyan-400/30 p-3 md:p-4"
                  style={{
                    background: 'linear-gradient(180deg, #0F1A2B 0%, #0A1628 100%)',
                    boxShadow: '0 24px 64px rgba(0,212,255,0.18), 0 12px 36px rgba(0,0,0,0.5)',
                  }}
                  role="img"
                  aria-label="PCIe 6.0 PAM4 link assurance visual showing high-speed waveform evidence, board-level electronics, validation signals, and mission-assurance decision workflow."
                  data-testid="pcie-hero-visual"
                >
                  <svg viewBox="0 0 800 420" xmlns="http://www.w3.org/2000/svg" className="block w-full h-auto rounded-xl" aria-hidden="true">
                    <defs>
                      <linearGradient id="pcieBg" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#0A1628" />
                        <stop offset="100%" stopColor="#0F1A2B" />
                      </linearGradient>
                      <radialGradient id="pcieGlow" cx="50%" cy="50%" r="50%">
                        <stop offset="0%" stopColor="#00D4FF" stopOpacity="0.45" />
                        <stop offset="60%" stopColor="#1B6EC2" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#0A1628" stopOpacity="0" />
                      </radialGradient>
                      <linearGradient id="goldTrace" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FBBF24" stopOpacity="0.9" />
                        <stop offset="100%" stopColor="#F59E0B" stopOpacity="0.4" />
                      </linearGradient>
                    </defs>
                    <rect width="800" height="420" fill="url(#pcieBg)" />
                    {/* faint scope grid */}
                    <g stroke="#1B6EC2" strokeOpacity="0.18" strokeWidth="0.5">
                      {[...Array(17)].map((_, i) => <line key={`v${i}`} x1={i * 50} y1="0"   x2={i * 50} y2="420" />)}
                      {[...Array(9)].map((_, i)  => <line key={`h${i}`} x1="0"      y1={i * 50} x2="800"      y2={i * 50} />)}
                    </g>
                    <circle cx="400" cy="210" r="240" fill="url(#pcieGlow)" />

                    {/* PAM4 eye diagram (4 levels, 3 stacked eyes) */}
                    <g transform="translate(110, 80)" stroke="#00D4FF" strokeWidth="1" fill="none" strokeOpacity="0.75">
                      {/* 4 horizontal PAM4 voltage levels reference */}
                      {[40, 95, 150, 205].map((y, i) => (
                        <line key={`l${i}`} x1="0" y1={y} x2="380" y2={y} strokeOpacity="0.18" strokeDasharray="3 4" />
                      ))}
                      {/* Layered curves forming three stacked eyes */}
                      {[
                        ['#00D4FF', 0.9],
                        ['#22D3EE', 0.55],
                        ['#1B6EC2', 0.4],
                        ['#67E8F9', 0.7],
                      ].map(([color, op], idx) => (
                        <g key={idx} stroke={color} strokeOpacity={op}>
                          {/* eye 1 (top) */}
                          <path d={`M0 ${40 + idx * 2} Q95 ${40 + idx * 2} 190 ${95 - idx} T380 ${40 + idx * 2}`} />
                          <path d={`M0 ${95 - idx} Q95 ${95 - idx} 190 ${40 + idx * 2} T380 ${95 - idx}`} />
                          {/* eye 2 (mid) */}
                          <path d={`M0 ${95 + idx} Q95 ${95 + idx} 190 ${150 - idx * 2} T380 ${95 + idx}`} />
                          <path d={`M0 ${150 - idx * 2} Q95 ${150 - idx * 2} 190 ${95 + idx} T380 ${150 - idx * 2}`} />
                          {/* eye 3 (bottom) */}
                          <path d={`M0 ${150 + idx * 2} Q95 ${150 + idx * 2} 190 ${205 - idx} T380 ${150 + idx * 2}`} />
                          <path d={`M0 ${205 - idx} Q95 ${205 - idx} 190 ${150 + idx * 2} T380 ${205 - idx}`} />
                        </g>
                      ))}
                      {/* center golden ring of compressed eye margin */}
                      <ellipse cx="190" cy="122" rx="38" ry="22" stroke="url(#goldTrace)" strokeWidth="1.5" fill="none" strokeOpacity="0.95" />
                      <ellipse cx="190" cy="177" rx="38" ry="22" stroke="url(#goldTrace)" strokeWidth="1.5" fill="none" strokeOpacity="0.85" />
                    </g>

                    {/* Annotation labels on the eye */}
                    <g fontFamily="Inter, Arial, sans-serif" fontSize="10" fill="#93C5FD" letterSpacing="1.5">
                      <text x="118"  y="76">  PAM4 EYE &middot; LEVELS 0/1/2/3</text>
                      <text x="118"  y="318" fill="#FBBF24">EYE HEIGHT &middot; EYE WIDTH</text>
                    </g>

                    {/* Right-side stats / module — board / package + BER badge */}
                    <g transform="translate(540, 60)">
                      <rect width="220" height="120" rx="8" fill="#0F1A2B" stroke="#00D4FF" strokeOpacity="0.5" />
                      <text x="14" y="22" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#00D4FF" letterSpacing="2">LINK METRICS</text>
                      <text x="14" y="48" fontFamily="Inter,Arial,sans-serif" fontSize="11" fill="#FFFFFF">BER (post-FEC)</text>
                      <text x="206" y="48" fontFamily="Inter,Arial,sans-serif" fontSize="13" fill="#34D399" textAnchor="end" fontWeight="bold">1e-15</text>
                      <text x="14" y="70" fontFamily="Inter,Arial,sans-serif" fontSize="11" fill="#FFFFFF">Insertion Loss</text>
                      <text x="206" y="70" fontFamily="Inter,Arial,sans-serif" fontSize="13" fill="#FBBF24" textAnchor="end" fontWeight="bold">-32 dB @ 16 GHz</text>
                      <text x="14" y="92" fontFamily="Inter,Arial,sans-serif" fontSize="11" fill="#FFFFFF">Eye Margin (vert)</text>
                      <text x="206" y="92" fontFamily="Inter,Arial,sans-serif" fontSize="13" fill="#FB923C" textAnchor="end" fontWeight="bold">62 mV</text>
                      <text x="14" y="114" fontFamily="Inter,Arial,sans-serif" fontSize="11" fill="#FFFFFF">Retimer EQ</text>
                      <text x="206" y="114" fontFamily="Inter,Arial,sans-serif" fontSize="13" fill="#67E8F9" textAnchor="end" fontWeight="bold">CTLE + DFE</text>
                    </g>

                    {/* Board / module schematic strip */}
                    <g transform="translate(540, 200)">
                      <rect width="220" height="130" rx="8" fill="#0F1A2B" stroke="#1B6EC2" strokeOpacity="0.5" />
                      <text x="14" y="22" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#67E8F9" letterSpacing="2">BOARD / PACKAGE</text>
                      <rect x="14" y="38" width="55" height="34" fill="#1B6EC2" fillOpacity="0.3" stroke="#00D4FF" strokeOpacity="0.6" />
                      <text x="42" y="58" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontSize="9" fill="#FFFFFF">CPU</text>
                      <rect x="153" y="38" width="55" height="34" fill="#1B6EC2" fillOpacity="0.3" stroke="#00D4FF" strokeOpacity="0.6" />
                      <text x="181" y="58" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontSize="9" fill="#FFFFFF">RETIMER</text>
                      <g stroke="#FBBF24" strokeOpacity="0.85" strokeWidth="1.5" fill="none">
                        <path d="M70 55 L152 55" strokeDasharray="0" />
                        <path d="M70 50 L152 50" strokeDasharray="4 3" />
                      </g>
                      <text x="111" y="44" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontSize="8" fill="#FBBF24">PCIe6 x16 @ 64 GT/s</text>
                      <text x="14" y="98" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#93C5FD">via &middot; stackup &middot; connector</text>
                      <text x="14" y="118" fontFamily="Inter,Arial,sans-serif" fontSize="10" fill="#93C5FD">package escape &middot; thermal</text>
                    </g>

                    {/* Bottom decision flow strip */}
                    <g transform="translate(110, 350)">
                      {['CHANNEL', 'VALIDATION', 'BOARD', 'SOURCE', 'MFG', 'DECISION'].map((label, i) => (
                        <g key={label} transform={`translate(${i * 110}, 0)`}>
                          <rect width="100" height="34" rx="6" fill="#1A2744" stroke="#00D4FF" strokeOpacity="0.35" />
                          <text x="50" y="22" textAnchor="middle" fontFamily="Inter,Arial,sans-serif" fontSize="9" fill="#67E8F9" letterSpacing="1.2">{label}</text>
                          {i < 5 && <text x="105" y="22" fontFamily="Inter,Arial,sans-serif" fontSize="14" fill="#00D4FF" fillOpacity="0.6">›</text>}
                        </g>
                      ))}
                    </g>
                  </svg>
                </div>
              </div>

              {/* Right side: START HERE download card */}
              <div className="lg:col-span-5 order-1 lg:order-2">
                <div
                  className="rounded-xl border border-amber-400/45 p-5 md:p-6"
                  style={{
                    background: 'linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(10,22,40,0.55) 60%, rgba(15,26,43,0.85) 100%)',
                    boxShadow: '0 10px 36px rgba(245,158,11,0.10), 0 4px 18px rgba(0,0,0,0.4)',
                  }}
                  data-testid="pcie-deck-card"
                >
                  <div className="flex items-start gap-4">
                    <div
                      className="flex-shrink-0 w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.55)' }}
                      aria-hidden="true"
                    >
                      <FileDown size={22} className="text-amber-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <Badge variant="outline" className="border-amber-400 text-amber-300 text-[10px] tracking-widest">
                          START HERE
                        </Badge>
                        <span className="text-[10px] text-amber-200/80 tracking-widest uppercase">PPTX &middot; 18 SLIDES</span>
                      </div>
                      <h3 className="text-lg md:text-xl font-bold text-white leading-snug mb-2">
                        Download the PCIe 6.0 / PAM4 Link Assurance Brief
                      </h3>
                      <p className="text-sm text-blue-100/95 leading-relaxed mb-4">
                        18-slide partner brief for high-speed interconnect assurance, board / package
                        reliability, supplier provenance, manufacturing stability, and decision-ready
                        evidence.
                      </p>
                      <a
                        href="/documents/SALAR_PCIe6_PAM4_Link_Assurance_Website_Upgrade_Brief_2026-05-12.pptx"
                        download
                        data-testid="pcie-deck-download-btn"
                        aria-label="Download PCIe 6.0 PAM4 Link Assurance partner brief, PowerPoint deck, 18 slides"
                        onClick={() => {
                          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                            window.gtag('event', 'file_download', {
                              file_name: 'SALAR_PCIe6_PAM4_Link_Assurance_Website_Upgrade_Brief_2026-05-12',
                              file_extension: 'pptx',
                              link_url: '/documents/SALAR_PCIe6_PAM4_Link_Assurance_Website_Upgrade_Brief_2026-05-12.pptx',
                            });
                          }
                        }}
                      >
                        <Button className="bg-amber-500 hover:bg-amber-600 text-[#0a1628] font-bold">
                          <Download className="mr-2" size={16} />
                          Download PPTX
                        </Button>
                      </a>
                      <div className="mt-3 text-[11px] text-blue-300/75 leading-relaxed">
                        PowerPoint deck &middot; 18 slides &middot; Partner / technical overview
                      </div>
                    </div>
                  </div>
                </div>

                {/* Positioning note */}
                <p className="mt-4 text-xs text-blue-200/85 italic leading-relaxed">
                  SALAR does not claim to be a PCIe compliance lab, PHY IP vendor, or replacement for
                  signal-integrity test equipment. SALAR helps make high-speed link evidence
                  decision-ready, traceable, and defensible for partners, labs, OEMs, boards, packages,
                  and mission electronics programs.
                </p>
              </div>
            </div>

            {/* Three value cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
              {[
                { id: 'evidence', Icon: Activity,    c: '#00D4FF', t: 'Link Evidence',         d: 'S-parameters, TDR, insertion loss, BER/FEC behavior, equalization training, eye metrics, and test repeatability.' },
                { id: 'board',    Icon: CircuitBoard,c: '#FBBF24', t: 'Board / Package Risk',  d: 'Stackup variation, vias, connectors, retimers, package escape, thermal hotspots, power noise, and rework history.' },
                { id: 'decision', Icon: ListChecks,  c: '#A78BFA', t: 'Decision Record',        d: 'P1–P5 link-health score, risk band, root-cause map, mitigation order, and partner workshare handoff.' },
              ].map((v) => (
                <div
                  key={v.id}
                  data-testid={`pcie-value-${v.id}`}
                  className="rounded-xl p-5 border"
                  style={{ background: '#1A2744', borderColor: `${v.c}55`, borderLeft: `3px solid ${v.c}` }}
                >
                  <div className="w-10 h-10 rounded-md flex items-center justify-center mb-3" style={{ background: `${v.c}18`, border: `1px solid ${v.c}60` }}>
                    <v.Icon size={18} style={{ color: v.c }} />
                  </div>
                  <h3 className="text-base font-bold text-white mb-2">{v.t}</h3>
                  <p className="text-xs text-blue-100/90 leading-relaxed">{v.d}</p>
                </div>
              ))}
            </div>

            {/* Five-Pillar Link Health strip */}
            <div className="mb-8">
              <div className="text-cyan-300 text-[10px] font-bold tracking-widest uppercase text-center mb-3">
                Five-Pillar Link Health Model
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="pcie-pillars-strip">
                {[
                  { id: 'P1', c: '#1B6EC2', t: 'Board / Design Reliability' },
                  { id: 'P2', c: '#22D3EE', t: 'Evidence / Verification Readiness' },
                  { id: 'P3', c: '#F59E0B', t: 'Supplier / Provenance Intelligence' },
                  { id: 'P4', c: '#FB923C', t: 'Manufacturing / Repairability' },
                  { id: 'P5', c: '#A78BFA', t: 'Qualification / Process Assurance' },
                ].map((p) => (
                  <div
                    key={p.id}
                    className="rounded-lg px-4 py-3 border text-center"
                    style={{ background: '#0F1A2B', borderColor: `${p.c}55`, borderTop: `3px solid ${p.c}` }}
                  >
                    <div className="text-xs font-bold tracking-widest mb-1" style={{ color: p.c }}>{p.id}</div>
                    <div className="text-[11px] text-white font-semibold leading-snug">{p.t}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Evidence chain strip */}
            <div
              className="rounded-xl p-4 md:p-5 border border-cyan-500/30 mb-8"
              style={{ background: '#0F1A2B' }}
              data-testid="pcie-evidence-chain"
            >
              <div className="text-cyan-300 text-[10px] font-bold tracking-widest uppercase mb-3 text-center">
                Evidence Chain
              </div>
              <div className="flex flex-wrap gap-2 justify-center items-center text-xs md:text-sm">
                {['Channel data', 'Validation evidence', 'Board evidence', 'Source evidence', 'Manufacturing evidence', 'Decision output'].map((s, i, arr) => (
                  <React.Fragment key={s}>
                    <span className="px-3 py-1.5 rounded-full border border-cyan-500/40 text-cyan-100 bg-[#1A2744] whitespace-nowrap">
                      {s}
                    </span>
                    {i < arr.length - 1 && <ArrowRight size={14} className="text-cyan-300/70" aria-hidden="true" />}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-testid="pcie-cta-row">
              <Link to="/contact" data-testid="pcie-cta-discussion">
                <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                  <Mail className="mr-2" size={18} />
                  Request Link Assurance Discussion
                </Button>
              </Link>
              <Link to="/ai-fabric-assurance" data-testid="pcie-cta-aifabric">
                <Button size="lg" variant="outline" className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]">
                  View AI Fabric Assurance
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Credibility Strip */}
      <section
        className="py-12 border-y border-cyan-500/20"
        style={{ background: '#0F1A2B' }}
        data-testid="founder-credibility"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="flex-shrink-0">
              <div
                className="rounded-lg border-2 border-cyan-400/40 p-4 flex items-center justify-center"
                style={{ background: '#0a1628', minWidth: 120, minHeight: 120 }}
                data-testid="salar-st-badge"
              >
                <img
                  src="/salar-logo-hd.png"
                  alt="SALAR Technologies"
                  className="h-16 w-auto object-contain"
                />
              </div>
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="text-cyan-300 text-xs font-bold uppercase tracking-widest mb-2">
                Founder &amp; Engineering Lead
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Vikrant Salar</h3>
              <p className="text-blue-100 leading-relaxed">
                21 years of hands-on engineering across SoC/ASIC design, hardware reliability, supply-chain
                risk analytics, and advanced semiconductor manufacturing. Mission Assurance is built on this
                discipline &mdash; founder-led decision support, not delegated review.
              </p>
              <div className="flex flex-wrap gap-3 mt-4 justify-center md:justify-start">
                <Badge variant="outline" className="border-cyan-400 text-cyan-200 text-xs">
                  USPTO &middot; 7 Pending Patents
                </Badge>
                <Badge variant="outline" className="border-cyan-400 text-cyan-200 text-xs">
                  Oregon LLC &middot; 254232599
                </Badge>
                <Badge variant="outline" className="border-cyan-400 text-cyan-200 text-xs">
                  CAGE 1AXR9
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="py-20 text-white"
        style={{
          backgroundImage: `linear-gradient(rgba(10,22,40,0.85), rgba(10,22,40,0.95)), url('/img/data_center_servers.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
        data-testid="mission-final-cta"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Have one board, BOM, or sustainment problem that needs a decision?
            </h2>
            <p className="text-lg text-blue-100 leading-relaxed mb-8">
              Book an intro call with SALAR Technologies LLC. We'll align on the program, the inputs you have today,
              and the decision file that would unlock the most value first.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={bookIntroHref} data-testid="cta-book-intro-call">
                <Button size="lg" className="bg-white text-[#0a1628] hover:bg-slate-100 font-semibold">
                  <Mail className="mr-2" size={20} />
                  Book Intro Call
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-[#0a1628]" data-testid="cta-contact">
                  Contact SALAR
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MissionThread;
